import type { LeaveAnswer } from "./leave-desk.ts";

/**
 * A chosen leave as the page runs it (P2.3-T4, ADR-084's amendment of 2026-10-13, item 10): the game menu's Change
 * character and Log out ask the gateway to leave before the page goes anywhere. In combat the gateway refuses with
 * the ticks left and the page stays in the world; otherwise the character stays in the world for the answer's ticks,
 * the page counts them down, and then it goes where it was going. The flow is the page's order made explicit, so it
 * is unit-tested without a page; `main.ts` gives it the page's parts.
 */

/**
 * What a chosen leave needs from the page.
 */
export interface ChosenLeavePage {
  /** Asks the gateway to leave (`GameScene.askLeave` with `confirm` true); null when there was no answer. */
  readonly askLeave: () => Promise<LeaveAnswer | null>;
  /** Tells the menu a fight locks the leave for a number of ticks. */
  readonly locked: (ticks: number) => void;
  /** Shows the stay's countdown and settles when it has run out. */
  readonly stay: (ticks: number) => Promise<void>;
}

/**
 * Leaves the world by the player's choice.
 *
 * @param page What the flow needs from the page.
 * @returns True when the page goes on leaving: the gateway took the leave and the stay has run out, or nobody
 *   answered, in which case the page leaves as a closed tab does. False when a fight refused the leave, so the page
 *   stays in the world.
 */
export async function leaveByChoice(page: ChosenLeavePage): Promise<boolean> {
  const answer = await page.askLeave();
  if (answer === null) {
    return true;
  }
  if (answer.lock > 0) {
    page.locked(answer.lock);
    return false;
  }
  if (answer.stay > 0) {
    await page.stay(answer.stay);
  }
  return true;
}
